import Pay from '@pay-com/js';
import type {
  InferredPayComExistingSourceType,
  InferredPayComPaymentMethodType,
  PayComCheckoutState,
  SavedBankInstrument,
  SavedCardInstrument,
} from './types';

export const DEFAULT_CHECKOUT_STATE: PayComCheckoutState = {
  checkoutObject: null,
  sessionId: null,
  sessionExpiresAt: null,
  allowedPaymentMethods: [],
  allowedCardBrands: [],
  savedCards: [],
  savedBankAccounts: [],
  savedCardsLoaded: false,
  googlepay: false,
  applepay: false,
  walletsDetected: false,
  isInitializing: true,
  isReady: false,
  isFailed: false,
  error: null,
};

// Fallback when Pay.com's `canMakePayments()` is slow or never resolves.
export function detectWalletsNatively(): { applepay: boolean; googlepay: boolean } {
  if (typeof window === 'undefined') return { applepay: false, googlepay: false };
  const applepay =
    'ApplePaySession' in window &&
    typeof (window as { ApplePaySession?: { canMakePayments?: () => boolean } }).ApplePaySession?.canMakePayments ===
      'function' &&
    (window as { ApplePaySession: { canMakePayments: () => boolean } }).ApplePaySession.canMakePayments() === true;
  const googlepay = 'PaymentRequest' in window;
  return { applepay, googlepay };
}

/**
 * True when a rejected pay call means "the user backed out" rather than a
 * payment failure — dismissing the Apple/Google Pay sheet, aborting a payment
 * request. Those must not surface an error banner.
 *
 * The SDK loads from Pay.com's CDN, so the rejection shape isn't typed:
 * observed/known shapes are Google Pay's raw `{statusCode: 'CANCELED'}`, the
 * SDK's `CANCELLED` transaction status, and browser `AbortError`s ("The user
 * aborted a request"), so this matches cancel/abort wording across the common
 * fields rather than one exact shape.
 */
export function isPaymentCancelledByUser(err: unknown): boolean {
  const isCancelWording = (value: unknown): value is string =>
    typeof value === 'string' && /cancel|abort|dismissed|closed/i.test(value);
  if (isCancelWording(err)) return true;
  if (!err || typeof err !== 'object') return false;
  const e = err as {
    statusCode?: unknown;
    code?: unknown;
    name?: unknown;
    message?: unknown;
    transactionStatus?: unknown;
    data?: { code?: unknown; message?: unknown };
  };
  if ([e.statusCode, e.code, e.name, e.message, e.transactionStatus, e.data?.code, e.data?.message].some(isCancelWording)) {
    return true;
  }
  // Deep fallback: the rejection shapes vary by wallet and SDK version, so
  // scan the whole serialized value for cancel wording wherever it's nested.
  // (Error instances serialize to '{}', but their message/name are covered by
  // the field checks above.)
  try {
    return /cancel|abort/i.test(JSON.stringify(err));
  } catch {
    return false;
  }
}

/**
 * True when a rejected pay call carries something user-presentable — a
 * non-empty message on the error itself or nested in `data`. Wallet-sheet
 * rejections with NO signal at all (undefined, `{}`, empty message) are
 * indistinguishable from dismissals and shouldn't surface a generic error.
 */
export function hasPaymentFailureSignal(err: unknown): boolean {
  if (typeof err === 'string') return err.trim().length > 0;
  if (!err || typeof err !== 'object') return false;
  const e = err as { message?: unknown; data?: { message?: unknown } };
  const message = typeof e.message === 'string' ? e.message : typeof e.data?.message === 'string' ? e.data.message : '';
  return message.trim().length > 0;
}

// Defaults to sandbox until VITE_PAY_COM_SANDBOX is explicitly set to 'false'.
export async function initializePayDotComInstance(merchantId: string) {
  if (!merchantId) {
    throw new Error('Merchant ID is required to initialize Pay.com instance');
  }
  const isSandbox = import.meta.env.VITE_PAY_COM_SANDBOX !== 'false';
  return Pay.com({
    identifier: merchantId,
    sandbox: isSandbox,
    live: !isSandbox,
  });
}

export function handlePaymentMethods(methods: InferredPayComPaymentMethodType[]): {
  allowedPaymentMethods: string[];
  allowedCardBrands: string[];
} {
  const allowedPaymentMethods: string[] = [];
  const allowedCardBrands = new Set<string>();

  methods.forEach((method) => {
    allowedPaymentMethods.push(method.paymentMethodType);
    method.supportedCardBrands?.forEach((brand) => allowedCardBrands.add(brand));
  });

  return {
    allowedPaymentMethods,
    allowedCardBrands: Array.from(allowedCardBrands),
  };
}

// Pay.com returns sources of every enabled type; we project them into the
// two shapes the UI consumes — saved cards (deposit + card withdraw) and
// saved US bank accounts (bank withdraw). Each call returns the same
// source list regardless of which payment-method-type the current session
// was minted for, so we always split eagerly.
export function handleExistingSources(sources: InferredPayComExistingSourceType[]): {
  cards: SavedCardInstrument[];
  bankAccounts: SavedBankInstrument[];
} {
  const cards: SavedCardInstrument[] = [];
  const bankAccounts: SavedBankInstrument[] = [];
  sources.forEach((source) => {
    if (source.type === 'card' && source.card) {
      cards.push({
        id: source.id,
        bin: source.card.bin,
        last4: source.card.last4,
        brand: source.card.brand,
        // `cvvExists` is missing from the SDK's public types but Pay.com still
        // emits it on the wire — needed to decide whether to re-prompt for CVV.
        hasCVV: !!(source.card as unknown as { cvvExists?: boolean }).cvvExists,
      });
      return;
    }
    // SDK types don't model us_bank_account yet; read defensively. On the wire
    // Pay.com returns `{ type: 'us_bank_account', bankAccount: { last4, bankName,
    // accountType, accountHolderName, ... } }` — all camelCase. We keep the
    // snake_case `us_bank_account` fallback so SETUP_SUCCESS event payloads
    // (which historically used Stripe-style snake_case) still parse.
    const sourceType = (source as unknown as { type: string }).type;
    if (sourceType !== 'us_bank_account') return;
    const bank =
      (source as unknown as { bankAccount?: Record<string, unknown> }).bankAccount ??
      (source as unknown as { us_bank_account?: Record<string, unknown> }).us_bank_account ??
      (source as unknown as { payment_method_details?: { us_bank_account?: Record<string, unknown> } })
        .payment_method_details?.us_bank_account;
    if (!bank) return;
    const rawAccountType = (bank.accountType ?? bank.account_type) as unknown;
    const accountType = rawAccountType === 'savings' ? 'savings' : 'checking';
    const last4 = bank.last4;
    const bankName = bank.bankName ?? bank.bank_name;
    const accountHolderName = bank.accountHolderName ?? bank.account_holder_name;
    bankAccounts.push({
      id: source.id,
      last4: typeof last4 === 'string' ? last4 : '',
      bankName: typeof bankName === 'string' ? bankName : '',
      accountType,
      accountHolderName: typeof accountHolderName === 'string' ? accountHolderName : null,
    });
  });
  return { cards, bankAccounts };
}
